import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Volume2, VolumeX, SkipForward } from "lucide-react";
import { useSiteContent } from "@/context/SiteContentContext";

const todayKey = () => new Date().toISOString().slice(0, 10);

export function isAnnouncementActive(a) {
  if (!a || !a.enabled || (!a.image && !a.video)) return false;
  const t = todayKey();
  if (a.start_date && t < a.start_date) return false;
  if (a.end_date && t > a.end_date) return false;
  return true;
}

const cardMotion = {
  initial: { y: -80, opacity: 0, scale: 0.98 },
  animate: { y: 0, opacity: 1, scale: 1 },
  exit: { y: -40, opacity: 0 },
  transition: { type: "spring", stiffness: 210, damping: 26, mass: 0.9 },
};

export default function AnnouncementPopup() {
  const { content, resolveImg } = useSiteContent();
  const a = content?.announcement;
  const active = isAnnouncementActive(a);
  const storageKey = active ? `announcement-dismissed:${a.video || ""}:${a.image}:${a.title || ""}` : null;
  const [open, setOpen] = useState(false);
  const [stage, setStage] = useState("video");
  const [muted, setMuted] = useState(true);
  const videoRef = useRef(null);

  useEffect(() => {
    if (!active) return;
    if (sessionStorage.getItem(storageKey)) return;
    setStage(a.video ? "video" : "image");
    const t = setTimeout(() => setOpen(true), 700);
    return () => clearTimeout(t);
  }, [active, storageKey, a?.video]);

  const close = () => {
    setOpen(false);
    if (storageKey) sessionStorage.setItem(storageKey, "1");
  };
  const toImage = () => (a.image ? setStage("image") : close());
  const toggleAudio = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
    if (!v.muted) v.play().catch(() => {});
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="announcement-overlay"
          className="fixed inset-0 z-[100] flex items-start justify-center bg-slate-900/70 backdrop-blur-sm px-4 pt-6 sm:pt-10 overflow-y-auto"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }}
          onClick={close}
        >
          <AnimatePresence mode="wait">
            {stage === "video" ? (
              <motion.div key="video" data-testid="announcement-video-card" {...cardMotion}
                className="relative w-full max-w-[860px] rounded-2xl bg-black shadow-2xl overflow-hidden"
                onClick={(e) => e.stopPropagation()}>
                <button type="button" aria-label="Chiudi" data-testid="announcement-video-close-x" onClick={close}
                  className="absolute top-3 right-3 z-10 h-9 w-9 rounded-full bg-white/90 text-slate-700 shadow flex items-center justify-center hover:bg-white transition-colors">
                  <X className="h-5 w-5" />
                </button>
                <video ref={videoRef} src={resolveImg(a.video)} autoPlay muted playsInline onEnded={toImage} onError={toImage}
                  className="block w-full min-h-[240px] max-h-[70vh] bg-black" data-testid="announcement-video" />
                <div className="flex items-center justify-between gap-3 px-4 py-3 bg-slate-950 border-t border-white/10">
                  <button type="button" data-testid="announcement-audio-toggle" onClick={toggleAudio}
                    className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm font-medium text-white hover:bg-white/20 transition-colors">
                    {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />} {muted ? "Attiva audio" : "Disattiva audio"}
                  </button>
                  <p className="hidden sm:block text-xs text-slate-400 truncate">{a.title || ""}</p>
                  <div className="flex items-center gap-2 shrink-0">
                    {a.image && (
                      <button type="button" data-testid="announcement-skip-button" onClick={toImage}
                        className="inline-flex items-center gap-1.5 rounded-full border border-white/30 px-4 py-2 text-sm font-medium text-white hover:bg-white/10 transition-colors">
                        <SkipForward className="h-4 w-4" /> Salta
                      </button>
                    )}
                    <button type="button" data-testid="announcement-video-close-button" onClick={close}
                      className="rounded-full bg-teal-600 px-5 py-2 text-sm font-semibold text-white hover:bg-teal-700 transition-colors">Chiudi</button>
                  </div>
                </div>
              </motion.div>
            ) : (
              <motion.div key="image" data-testid="announcement-popup" {...cardMotion}
                className="relative w-full max-w-[560px] rounded-2xl bg-white shadow-2xl overflow-hidden"
                onClick={(e) => e.stopPropagation()}>
                <button type="button" aria-label="Chiudi" data-testid="announcement-close-x" onClick={close}
                  className="absolute top-3 right-3 z-10 h-9 w-9 rounded-full bg-white/90 text-slate-700 shadow flex items-center justify-center hover:bg-white transition-colors">
                  <X className="h-5 w-5" />
                </button>
                {a.link ? (
                  <a href={a.link} target="_blank" rel="noreferrer"><img src={resolveImg(a.image)} alt={a.title || "Avviso"} className="block w-full min-h-[240px] max-h-[72vh] object-contain bg-slate-950" /></a>
                ) : (
                  <img src={resolveImg(a.image)} alt={a.title || "Avviso"} className="block w-full min-h-[240px] max-h-[72vh] object-contain bg-slate-950" />
                )}
                <div className="flex items-center justify-between gap-4 px-5 py-4 border-t border-slate-100">
                  <p className="text-sm text-slate-600 min-w-0 truncate">{a.title || "Avviso del laboratorio"}</p>
                  <button type="button" data-testid="announcement-close-button" onClick={close}
                    className="shrink-0 rounded-full bg-teal-600 px-6 py-2 text-sm font-semibold text-white shadow-sm hover:bg-teal-700 transition-colors">
                    Chiudi
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
